import React, { useEffect } from 'react';
import styled, { css } from 'styled-components';
import { motion } from 'framer-motion';
import Scene from '../scene/scene';
import { CallToAction } from '../../types/call-to-action';
import H4 from '../../modules/lower-ui-kit/components/typography/h4';
import { Button } from '../../modules/lower-ui-kit';
import { ButtonType } from '../../modules/lower-ui-kit/components/button/button';
import TextInput from '../../modules/lower-ui-kit/components/text-input/text-input';
import H5 from '../../modules/lower-ui-kit/components/typography/h5';
import { StyledWithChildrenProps } from '../../modules/lower-ui-kit/types/StyledWithChildrenProps';
// Framer Motion
import { getRandomDelay } from '../../modules/lower-ui-kit/lib/animation-functions/get-random-delay';
import { randomDuration } from '../../modules/lower-ui-kit/lib/animation-functions/random-duration';
import useDocumentTitle from '../../modules/lower-ui-kit/hooks/useDocumentTitle/useDocumentTitle';
import useNotifications from '../../hooks/useNotifications/useNotifications';
import { NotificationType, UserNotification } from '../../hooks/useNotifications/UserNotification';
import useLoginSceneModel from './login-scene-model';

const variants = {
  reset: {
    rotate: 0,
  },
  start: (any: number) => ({
    rotate: any % 2 === 0
      ? [-1, 1.3, 0]
      : [1, -1.4, 0],
    transition: {
      delay: getRandomDelay(),
      duration: randomDuration(),
      repeat: 1,
    },
  }),
};

type LoginPageProps = {
  title: string;
  subtitle: string;
  helperTitle: string;
  helperCopy: string;
  cta: CallToAction;
  pageTitle: string;
  loginErrorTitle: string;
  loginErrorMessage: string;
  genericErrorTitle: string;
  genericErrorMessage: string;
};

function LoginSceneView({
  title,
  subtitle,
  helperTitle,
  helperCopy,
  cta,
  pageTitle,
  loginErrorTitle,
  loginErrorMessage,
  genericErrorTitle,
  genericErrorMessage,
}: LoginPageProps) {
  const {
    controls, input, onVerify, setInput, isUserCodeValid, waiting, setUserCode, statusCode,
  } = useLoginSceneModel();

  const titlePrefix = 'DocUpload | ';

  useDocumentTitle(`${titlePrefix}${pageTitle}`);

  const { notify, clearNotifications } = useNotifications();

  const maintenanceInfo = process.env.MAINTENANCE_INFO ?? '';

  useEffect(() => {
    clearNotifications();

    const codeInfo: UserNotification = {
      message: helperCopy,
      notificationType: NotificationType.Info,
      title: helperTitle,
    };
    const errorInfo: UserNotification = {
      message: statusCode === 401 || statusCode === 404
        ? loginErrorMessage
        : genericErrorMessage,
      notificationType: NotificationType.Error,
      title: statusCode === 401 || statusCode === 404
        ? loginErrorTitle
        : genericErrorTitle,
    };

    if (input === '') {
      notify(codeInfo);
    } else if (!isUserCodeValid && input !== '' && !waiting)  {
      notify(errorInfo);
      setUserCode('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    waiting,
    isUserCodeValid,
  ]);

  return (
    <Scene>
      <Container>
        <div>
          <Title>
            {title}
          </Title>
          <MaintenanceInfo>
            {maintenanceInfo}
          </MaintenanceInfo>
          <Subtitle>
            {subtitle}
          </Subtitle>
        </div>
        <SubmissionForm>
          <motion.div
            variants={variants}
            animate={controls}
          >
            <InputDiv>
              <TextInput
                autoComplete={'off'}
                name={'AuthCode'}
                value={input}
                handleChange={(value) => setInput(value)}
                placeholder={'Enter code here'}
                maxLength={8}
              />
            </InputDiv>
          </motion.div>
          <SubmitButton
            onClick={onVerify}
            type={ButtonType.Form}
            active
          >
            {cta.label}
          </SubmitButton>
        </SubmissionForm>
      </Container>
    </Scene>
  );
}

const Title = styled(H5) < StyledWithChildrenProps > `
  font-weight: bold;
  letter-spacing: -0.32px;
  line-height: 28px;
  font-size: 18px;
  margin-bottom: 14px;
  ${({ theme }) => css`
    color: ${theme.colors.primary};
  `}
`;

const Subtitle = styled(H4) < StyledWithChildrenProps > `
  font-weight: bold;
  letter-spacing: -2px;
  line-height: 70px;
  font-size: 60px;
  max-width: 790px;
  margin: 0 auto;
  margin-bottom: 35px;
  ${({ theme }) => css`
    color: ${theme.palette.grey600};
    ${theme.media.tablet} {
      font-size: 48px;
      line-height: 56px;
      letter-spacing: -1.73px;
      max-width: 320px;
      margin-bottom: 25px;
    }
  `}
`;

const MaintenanceInfo = styled(H5) < StyledWithChildrenProps > `
  ${({ theme }) => css`
    font-size: ${theme.maintenanceInfo.fontSize};
    color: ${theme.maintenanceInfo.color};
    margin: ${theme.maintenanceInfo.margin};
  `}
`;

const InputDiv = styled.div`
  display: inline-block;
  position: relative;
  max-width:  ${({ theme }) => theme.inputType.maxWidth};
  margin-bottom: 40px;
  ${({ theme }) => theme.media.tablet} {
    margin-bottom: 20px;
  }
`;

const SubmissionForm = styled.form`
  display: flex;
  flex-direction: column;
  max-width:  ${({ theme }) => theme.inputType.maxWidth};
  margin: 54px auto 34px auto;
  ${({ theme }) => theme.media.tablet} {
    margin: 14px auto 54px auto;
  }
`;

const SubmitButton = styled(Button)`
  ${({ theme }) => css`
    cursor: pointer;
    margin-top: 16px;
    max-width:  ${theme.buttonType.maxWidth};
    margin:  ${theme.buttonType.margin};
  `}
`;

const Container = styled.div`
  text-align: center;
  margin-top: 10vmin;
`;

export default LoginSceneView;
